import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Segment } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardCommon from '@op/components/CardCommon';
import {
  arrayToSimpleObj,
  getPercentageInfo,
  supplementSingleData,
} from '@op/utils';
import ChannelLabel from '@op/components/ChannelLabel';
import { getOverviewTimeline } from '@op/services/performance';
import LineCharts from '../components/LineCharts';
import useDealwithPlatform from '../hooks/useDealwithPlatform';
import { formatSimpleTimeFunc } from '@/pages/opinion/utils/time';
import { useRequestLifetimePcConsole } from '../hooks/useRequestLifetimePcConsole';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import UserScoreBox from '@/pages/opinion/components/UserScoreCommon';
import useGetGameStoreStatsInfo from '../hooks/useGetGameStoreStatsInfo';
import useGetGameStoreTimelineInfo from '../hooks/useGetGameStoreTimelineInfo';
import { GameStoreMobileChart } from '../components/GameStoreMobileChart';
import { NO_DATE } from '@/pages/opinion/constants';
import SteamLineReviews from '../components/SteamLineReviews';
import PlatformTitle from '../components/PlatformTitle';
import {
  steamNegativeHoverTips,
  steamPositiveHoverTips,
} from '@/pages/opinion/components/BubbleCommon';

export default function GameStorePc({
  filter,
  gameInfo,
  handleTitleOnClick,
}: any) {
  // store summary数据
  const [steamReviewInfo, setSteamReviewInfo] = useState<{
    recommend: number;
    non_recommend: number;
  }>({ recommend: 0, non_recommend: 0 });
  // store pc  Selected 折线图数据
  const [pcSelectedLineData, setPcSelectedLineData] = useState<any>({
    recommend: [],
    non_recommend: [],
    xAxis: [],
  });
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const getPcGameStoreData = () => {
    setLoading(true);
    // const { social_media_by_account, social_media_by_keyword, ...otherFilter } =
    //   filter;
    getOverviewTimeline({
      ...filter,
      ...gameInfo,
      channel: ['steam'],
      channel_type: 'comments',
      metrics: ['recommend', 'non_recommend'],
      ret_metrics: ['recommend', 'non_recommend'],
      types: ['total', 'slice'],
    }).then((data: any) => {
      let { list, timeline_of_end_time, timeline_of_start_time, total_list } =
        data || {};
      let newList = supplementSingleData(
        timeline_of_start_time,
        timeline_of_end_time,
        list?.map((item: any) => ({
          ...item,
          name: item.key,
          count: item.value,
        })),
        filter.date_type,
        ['recommend', 'non_recommend'],
      );
      let result: any = {
        recommend: [],
        non_recommend: [],
      };
      let xAxis = new Set();
      newList.forEach(({ name, time, count }: any) => {
        if (!result[name]) {
          result[name] = [];
        }
        xAxis.add(time);
        result[name].push(count);
      });

      const reviewsInfo: any = arrayToSimpleObj(total_list, 'value', 'key');

      setSteamReviewInfo(reviewsInfo);
      setPcSelectedLineData({ ...result, xAxis: Array.from(xAxis) });

      setLoading(false);
    });
  };

  const params = useMemo(() => {
    if (filter && gameInfo) {
      const { channel, ...otherFilter } = filter;
      return {
        ...otherFilter,
        ...gameInfo,
        channels: ['opencritic', 'metacritic', 'steam'],
        device: '',
      };
    }

    return {};
  }, [filter]);

  const {
    startTime,
    endTime,
    changeTime,
    loading: pcConsoleLoading,
    data: pcCriticData,
    show,
    time,
    isToday,
  } = useRequestLifetimePcConsole({
    filter: params,
  });

  const [xboxValue, setXboxValue] = useState<'selected' | 'lifetime'>(
    'selected',
  );
  const [activeTab, setActiveTab] = useState<any>('discount-chart');

  const {
    loading: statsLoading,
    ratingInfo,
    ratings,
  } = useGetGameStoreStatsInfo({
    gameInfo,
    filter,
    type: 'xbox',
    lifetimeTitle: 'Lifetime Reviews',
    selectedTitle: 'Added Reviews',
  });

  const {
    trendLoading,
    nodata,
    mobileTrendsInfo: xboxTrendsInfo,
  } = useGetGameStoreTimelineInfo({ gameInfo, filter, type: 'xbox' });

  const xboxData = useMemo(() => {
    return ratingInfo?.[xboxValue] || {};
  }, [xboxValue, ratingInfo]);

  const xboxTrendsList = useMemo(() => {
    return xboxTrendsInfo?.[xboxValue] || {};
  }, [xboxValue, xboxTrendsInfo]);

  const xboxRenderText = useMemo(() => {
    return ratings.find((item) => item.ratingKey === xboxValue);
  }, [xboxValue, ratings]);

  useEffect(() => {
    if (filter && gameInfo) {
      getPcGameStoreData();
    }
  }, [filter, gameInfo]);

  const [steamRenderReviewList, reviewTotal] = useMemo(() => {
    let { recommend, non_recommend } = steamReviewInfo || {};
    recommend = recommend || 0;
    non_recommend = non_recommend || 0;
    const total = recommend + non_recommend;
    const steamPositivePercent = getPercentageInfo(recommend, total, 2, '0%');
    const steamNegativePercent = getPercentageInfo(
      non_recommend,
      total,
      2,
      '0%',
    );
    return [
      [
        {
          color: '#69D19C',
          text: 'Positive',
          width: steamPositivePercent,
          value: recommend,
          icon: 'good-icon',
          tipsMap: steamPositiveHoverTips,
        },
        {
          color: '#F17C86',
          text: 'Negative',
          width: steamNegativePercent,
          value: non_recommend,
          icon: 'no-good-icon',
          tipsMap: steamNegativeHoverTips,
        },
      ],
      total,
    ];
  }, [steamReviewInfo]);

  const { metacriticElement, opencriticElement, steamElement } =
    useDealwithPlatform({
      data: pcCriticData,
      loading: pcConsoleLoading,
      show: show,
      setTime: changeTime,
      isToday: isToday,
    });
  const [segmentValue, setSegmentValue] = useState<any>('selected');

  const getSteamELement = () => {
    if (segmentValue === 'selected') {
      return (
        <>
          {/* review 进度条 与总数*/}
          <SteamLineReviews
            reviewTotal={reviewTotal}
            steamRenderReviewList={steamRenderReviewList}
          ></SteamLineReviews>

          <div className="context-item-data">
            <LineCharts
              dateType={filter?.date_type}
              lineData={pcSelectedLineData}
              loading={loading}
              height={130}
            ></LineCharts>
          </div>
        </>
      );
    }

    return (
      <div className="context-item-progress">
        <div className="dbo-context-item-progress">{steamElement}</div>
      </div>
    );
  };

  return (
    <Row className="dbo-cardrow-wrap">
      <Col>
        <CardCommon
          className="dbo-gamestorecard dbo-gamestorecard--pc"
          title={formatMessage({ id: 'Game Store' })}
          hasTitleArrow
          handleTitleOnClick={() => handleTitleOnClick?.()}
        >
          <div className="gamestore-pc-context">
            <div className="context-item">
              <PlatformTitle channelName="steam" title="Steam"></PlatformTitle>

              <Segment
                className="gamestore-pc-context-segment"
                value={segmentValue}
                onChange={(value) => setSegmentValue(value)}
                options={formatOptionsMessage([
                  { text: 'Added Reviews', value: 'selected' },
                  { text: 'Lifetime Reviews', value: 'lifetime' },
                ])}
              />
              {getSteamELement()}
            </div>
            <div className="context-item">
              <div className="context-item-title">
                <ChannelLabel
                  channel_name={'opencritic'}
                  showText={false}
                  style={{ marginRight: 10 }}
                ></ChannelLabel>
                <span className="context-item-title-text">OpenCritic</span>
              </div>
              <div className="context-item-flex">
                <div className="context-item-flex-title">
                  {formatMessage({ id: 'Lifetime Reviews' })}
                </div>
                <div className="context-item-flex-time">
                  {time.opencritic &&
                    formatMessage({ id: 'Up to' }) +
                    ' ' +
                    formatSimpleTimeFunc(time.opencritic)}
                </div>
              </div>

              <div className="context-item-progress">{opencriticElement}</div>
            </div>
            <div className="context-item">
              <div className="context-item-title">
                <ChannelLabel
                  channel_name={'metacritic'}
                  showText={false}
                  style={{ marginRight: 10 }}
                ></ChannelLabel>

                <span className="context-item-title-text">MetaCritic</span>
              </div>
              <div className="context-item-flex">
                <div className="context-item-flex-title">
                  {formatMessage({ id: 'Lifetime Reviews' })}
                </div>
                <div className="context-item-flex-time">
                  {time.metacritic &&
                    formatMessage({ id: 'Up to' }) +
                    ' ' +
                    formatSimpleTimeFunc(time.metacritic)}
                </div>
              </div>
              <div className="context-item-progress">{metacriticElement}</div>
            </div>
            <div className="context-item">
              <div className="context-item-title">
                <ChannelLabel
                  channel_name={'xbox'}
                  showText={false}
                  style={{ marginRight: 10 }}
                ></ChannelLabel>
                <span className="context-item-title-text">Xbox</span>
              </div>
              <Segment
                className="gamestore-pc-context-segment"
                value={xboxValue}
                onChange={(value) => {
                  setXboxValue(value);
                  if (value == 'selected') {
                    setActiveTab('discount-chart');
                  } else {
                    setActiveTab('histogram-skew');
                  }
                }}
                options={formatOptionsMessage([
                  { text: 'Added Reviews', value: 'selected' },
                  { text: 'Lifetime Reviews', value: 'lifetime' },
                ])}
              />
              <div className="context-item-flex">
                <div className="context-item-flex-title">
                  {/* {xboxRenderText?.title} */}
                </div>
                <div className="context-item-flex-icon">
                  {['discount-chart', 'histogram-skew']?.map(
                    (e: any, i: any) => {
                      return (
                        <i
                          key={e}
                          className={
                            e == activeTab
                              ? 'card-slot-action'
                              : 'card-slot-item'
                          }
                          onClick={() => {
                            setActiveTab(e);
                          }}
                        >
                          <SvgIcon type={e} className="card-slot-charicon" />
                        </i>
                      );
                    },
                  )}
                </div>
              </div>
              <CardCommon
                contentCard
                loadingHeight={158}
                emptyHeight={158}
                hasEmptyImg={false}
                hasEmptyDesc={false}
                emptyTitleText={NO_DATE.SELECTION}
                empty={activeTab == 'discount-chart' ? nodata : false}
                loading={statsLoading || trendLoading}
              >
                <>
                  {activeTab == 'discount-chart' ? (
                    <div className="dbo-gamestore-line">
                      <GameStoreMobileChart
                        data={xboxTrendsList}
                        filter={filter}
                        height={158}
                        channel="xbox"
                        isMin
                        formatMessage={formatMessage}
                      ></GameStoreMobileChart>
                    </div>
                  ) : (
                    <UserScoreBox
                      type={xboxValue}
                      filter={filter}
                      platform="xbox"
                      data={xboxData}
                      height={158}
                      isFeeds={false}
                      isHeader={true}
                    />
                  )}
                </>
              </CardCommon>
            </div>
          </div>
        </CardCommon>
      </Col>
    </Row>
  );
}
